import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, FileText, Briefcase, ChevronDown } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api, mediaUrl, formatApiErrorDetail, fmtDateUS } from "@/lib/api";

const statusLabel = (s) =>
  s === "accepted" ? "Approved" :
  s === "edit_requested" ? "Changes requested" :
  s === "revision" ? "Updated — please re-review" :
  "Ready to review";

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [sending, setSending] = useState(null);
  const [extraNotes, setExtraNotes] = useState({});
  const [deciding, setDeciding] = useState(null);
  const [jobOpen, setJobOpen] = useState({});
  const [jobTabs, setJobTabs] = useState({});
  const [matAnswers, setMatAnswers] = useState({});

  const answerMaterial = async (jobId, orderId, status) => {
    try {
      await api.post(`/portal/${token}/jobs/${jobId}/material-orders/${orderId}/answer`, {
        status, eta: (matAnswers[orderId]?.eta || "").trim(), note: (matAnswers[orderId]?.note || "").trim(),
      });
      setMatAnswers((m) => ({ ...m, [orderId]: {} }));
      load();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const decideExtra = async (jobId, extraId, decision) => {
    setDeciding(extraId);
    try {
      await api.post(`/portal/${token}/jobs/${jobId}/extras/${extraId}`, { decision, client_note: (extraNotes[extraId] || "").trim() });
      load();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeciding(null);
    }
  };

  const load = () => {
    api.get(`/portal/${token}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  const sendAnswer = async (jobId, cid) => {
    const answer = (answers[cid] || "").trim();
    if (!answer) return;
    setSending(cid);
    try {
      await api.post(`/portal/${token}/jobs/${jobId}/clarifications/${cid}/answer`, { answer });
      setAnswers((a) => ({ ...a, [cid]: "" }));
      load();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(null);
    }
  };

  return (
    <div data-testid="portal-page" className="min-h-screen bg-background grain flex flex-col">
      <Header />
      <main className="flex-1 pt-16">
        {error && (
          <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-6" data-testid="portal-error">
            <p className="font-display text-4xl tracking-wide mb-3">LINK NOT FOUND</p>
            <p className="text-white/50 text-sm max-w-sm">{error}. Call us at (203) 307-5222 and we'll send you a fresh one.</p>
          </div>
        )}
        {!error && !data && (
          <p className="text-white/40 text-sm text-center py-24" data-testid="portal-loading">Loading your proposals...</p>
        )}
        {data && (
          <div className="max-w-2xl mx-auto px-5 py-12 md:py-16 w-full">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary mb-2">Infinite Siding LLC</p>
              <h1 className="font-display text-5xl md:text-6xl tracking-wide">HI {data.customer_name.toUpperCase()}</h1>
              <p className="mt-3 text-white/55 text-sm md:text-base">
                {data.proposals.length === 1
                  ? "You have 1 proposal with us — tap it to review."
                  : `You have ${data.proposals.length} proposals with us — pick the one you want to review.`}
              </p>
            </motion.div>

            <div className="mt-8 space-y-3" data-testid="portal-proposals">
              {data.proposals.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08, duration: 0.5 }}>
                  <Link to={`/proposal/${p.id}`} data-testid={`portal-proposal-${p.id}`} className="group flex items-center gap-4 border border-white/15 rounded-2xl bg-card px-5 py-5 transition-colors duration-300 hover:border-primary/50">
                    <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <FileText size={20} aria-hidden="true" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold text-sm md:text-base truncate">{p.title}</span>
                      <span className="block text-[11px] text-white/45 mt-0.5">
                        {fmtDateUS(p.created_at)}
                        {(p.accepted_total ?? p.base_total) > 0 && ` · $${(p.accepted_total ?? p.base_total).toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                      </span>
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shrink-0 ${p.status === "accepted" ? "bg-primary/15 text-primary" : "bg-white/5 text-white/60"}`}>
                      {statusLabel(p.status)}
                    </span>
                    <ArrowUpRight size={18} className="text-white/30 group-hover:text-primary transition-colors duration-300 shrink-0" aria-hidden="true" />
                  </Link>
                </motion.div>
              ))}
              {data.proposals.length === 0 && (
                <p className="text-white/40 text-sm text-center py-14 border border-dashed border-white/15 rounded-2xl" data-testid="portal-empty">
                  No proposals yet — call (203) 307-5222 and we'll get one ready for you.
                </p>
              )}
            </div>

            {data.jobs?.length > 0 && (
              <div className="mt-12" data-testid="portal-jobs">
                <h2 className="font-display text-3xl tracking-wide mb-1">YOUR PROJECTS</h2>
                <p className="text-white/45 text-xs mb-5">Live progress, invoices and open questions from our crew.</p>
                <div className="space-y-4">
                  {data.jobs.map((j) => {
                    const isOpen = jobOpen[j.id] ?? data.jobs.length === 1;
                    const tab = jobTabs[j.id] || "progress";
                    const TABS = [
                      ["progress", "Progress"],
                      ["updates", "Updates"],
                      ["invoices", "Invoices"],
                      ["messages", "Messages"],
                      ...(j.supply_mode === "labor" ? [["materials", "Materials"]] : []),
                    ];
                    return (
                    <div key={j.id} className="border border-white/15 rounded-2xl bg-card overflow-hidden" data-testid={`portal-job-${j.id}`}>
                      <button onClick={() => setJobOpen({ ...jobOpen, [j.id]: !isOpen })} data-testid={`portal-job-toggle-${j.id}`} aria-expanded={isOpen} className="w-full text-left p-5 hover:bg-white/[0.03] transition-colors duration-200">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm md:text-base flex items-center gap-2"><Briefcase size={15} className="text-primary shrink-0" aria-hidden="true" /> {j.title}</p>
                            {j.address && <p className="text-[11px] text-white/45 mt-1">{j.address}</p>}
                          </div>
                          <ChevronDown size={17} className={`shrink-0 text-white/40 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                        </div>
                        <div className="mt-3 flex items-center gap-2.5">
                          <div className="h-2 flex-1 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${j.completion_pct}%` }} />
                          </div>
                          <span className="font-display text-xl text-primary shrink-0" data-testid={`portal-job-pct-${j.id}`}>{j.completion_pct}%</span>
                        </div>
                      </button>

                      {isOpen && (
                      <div className="px-5 pb-5">
                      <div className="flex gap-1 overflow-x-auto -mx-1 px-1 pb-1" data-testid={`portal-tabs-${j.id}`}>
                        {TABS.map(([key, label]) => (
                          <button key={key} onClick={() => setJobTabs({ ...jobTabs, [j.id]: key })} data-testid={`portal-tab-${key}-${j.id}`}
                                  className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest border rounded-lg whitespace-nowrap transition-colors duration-200 ${tab === key ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/50"}`}>
                            {label}
                          </button>
                        ))}
                      </div>

                      {tab === "progress" && (<>
                      {j.progress?.categories?.length > 0 && (
                        <div className="mt-3 space-y-2.5" data-testid={`portal-progress-${j.id}`}>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">Progress by area</p>
                          {j.progress.categories.map((c) => (
                            <div key={c.name} className="border border-white/10 rounded-lg bg-white/[0.03] px-3.5 py-3" data-testid={`portal-cat-${j.id}-${c.name}`}>
                              <div className="flex items-center justify-between text-xs mb-1.5">
                                <span className="font-semibold text-white/85">{c.name}</span>
                                <span className="text-primary font-bold" data-testid={`portal-cat-pct-${j.id}-${c.name}`}>{c.pct}%</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                                <div className="h-full bg-primary rounded-full" style={{ width: `${c.pct}%` }} />
                              </div>
                              <div className="mt-2 space-y-1.5">
                                {j.progress.items.filter((i) => i.category === c.name).map((i) => (
                                  <div key={i.id} className="flex items-center gap-2" data-testid={`portal-item-${i.id}`}>
                                    <span className="flex-1 min-w-0 text-[11px] text-white/60 truncate">{i.name}{i.source === "extra" ? " · extra work" : ""}</span>
                                    <div className="w-20 h-1 rounded-full bg-white/10 overflow-hidden shrink-0">
                                      <div className="h-full bg-primary/80 rounded-full" style={{ width: `${i.pct}%` }} />
                                    </div>
                                    <span className="text-[10px] text-white/50 w-9 text-right shrink-0" data-testid={`portal-item-pct-${i.id}`}>{i.pct}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {j.extras?.length > 0 && (
                        <div className="mt-5 space-y-2" data-testid={`portal-extras-${j.id}`}>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">Additional work — your decision</p>
                          {j.extras.map((e) => (
                            <div key={e.id} className={`border rounded-lg px-3.5 py-3 ${e.status === "pending" ? "border-amber-400/40 bg-amber-400/5" : "border-white/10 bg-white/[0.03]"}`} data-testid={`portal-extra-${e.id}`}>
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-semibold">{e.title}</p>
                                <p className="text-sm font-bold text-primary shrink-0">${e.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                              </div>
                              <p className="text-[11px] text-white/50 mt-0.5">{e.quantity} {e.unit} × ${e.rate.toLocaleString("en-US", { minimumFractionDigits: 2 })}{e.note ? ` · ${e.note}` : ""}</p>
                              {e.photo_path && (
                                <a href={mediaUrl(e.photo_path)} target="_blank" rel="noreferrer" className="inline-block mt-1.5" data-testid={`portal-extra-photo-${e.id}`}>
                                  <img src={mediaUrl(e.photo_path)} alt={e.title} loading="lazy" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
                                </a>
                              )}
                              {e.status === "pending" ? (
                                <div className="mt-2.5 space-y-2">
                                  <input value={extraNotes[e.id] || ""} onChange={(ev) => setExtraNotes({ ...extraNotes, [e.id]: ev.target.value })} placeholder="Note for us (optional)" data-testid={`portal-extra-note-${e.id}`} className="rounded-lg w-full bg-white/5 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300" />
                                  <div className="flex gap-2">
                                    <button onClick={() => decideExtra(j.id, e.id, "approve")} disabled={deciding === e.id} data-testid={`portal-extra-approve-${e.id}`} className="flex-1 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest py-2.5 rounded-lg disabled:opacity-50">
                                      {deciding === e.id ? "..." : "Approve"}
                                    </button>
                                    <button onClick={() => decideExtra(j.id, e.id, "deny")} disabled={deciding === e.id} data-testid={`portal-extra-deny-${e.id}`} className="flex-1 border border-red-400/40 text-red-300 text-xs font-bold uppercase tracking-widest py-2.5 rounded-lg disabled:opacity-50">
                                      Deny
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <p className={`text-[10px] font-bold uppercase tracking-widest mt-1.5 ${e.status === "approved" ? "text-primary" : "text-red-300"}`} data-testid={`portal-extra-status-${e.id}`}>
                                  {e.status === "approved" ? "Approved — added to your project total and next invoice" : "Declined"}{e.client_note ? ` · your note: ${e.client_note}` : ""}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      </>)}

                      {tab === "updates" && (<>
                      {j.daily_reports?.length > 0 && (
                        <div className="mt-3 space-y-2" data-testid={`portal-reports-${j.id}`}>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">Updates from the crew</p>
                          {j.daily_reports.map((r) => (
                            <div key={r.id} className="border border-white/10 rounded-lg bg-white/[0.03] px-3.5 py-3" data-testid={`portal-report-${r.id}`}>
                              <p className="text-[10px] text-white/40 uppercase tracking-widest font-bold">{fmtDateUS(r.report_date)}</p>
                              <p className="text-xs text-white/75 mt-1 leading-relaxed">{r.progress}</p>
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {r.delayed && <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300">Delayed{r.delay_reason ? `: ${r.delay_reason}` : ""}</span>}
                                {r.material_missing && <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-400/15 text-red-300">Material on the way{r.material_note ? `: ${r.material_note}` : ""}</span>}
                              </div>
                              {r.photos?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {r.photos.map((p) => (
                                    <a key={p} href={mediaUrl(p)} target="_blank" rel="noreferrer"><img src={mediaUrl(p)} alt="Daily report" loading="lazy" className="w-14 h-14 object-cover rounded-lg border border-white/10" /></a>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {j.progress_photos?.length > 0 && (
                        <div className="mt-5" data-testid={`portal-photos-${j.id}`}>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-white/45 mb-2">Progress photos from the crew</p>
                          <div className="grid grid-cols-3 gap-2">
                            {j.progress_photos.map((ph) => (
                              <a key={ph.id} href={mediaUrl(ph.storage_path)} target="_blank" rel="noreferrer" className="block" data-testid={`portal-photo-${ph.id}`}>
                                <img src={mediaUrl(ph.storage_path)} alt={ph.note || "Progress photo"} loading="lazy" className="w-full aspect-square object-cover rounded-lg border border-white/10" />
                                <p className="text-[10px] text-white/40 mt-1 truncate">{ph.note || "Progress"} · {fmtDateUS(ph.ts)}</p>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {!j.daily_reports?.length && !j.progress_photos?.length && (
                        <p className="text-[11px] text-white/35 mt-3">No updates yet — crew reports and photos show up here.</p>
                      )}
                      </>)}

                      {tab === "invoices" && (
                      <div className="mt-3 space-y-2" data-testid={`portal-invoices-${j.id}`}>
                        {j.invoices?.length > 0 ? j.invoices.map((inv) => (
                          <div key={inv.id} className="flex items-center justify-between gap-3 border border-white/10 rounded-lg bg-white/[0.03] px-3.5 py-2.5" data-testid={`portal-invoice-${inv.id}`}>
                            <p className="text-sm text-white/85">Invoice #{inv.number}{inv.note ? ` — ${inv.note}` : ""}</p>
                            <span className="flex items-center gap-2.5 shrink-0">
                              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/${token}/jobs/${j.id}/invoices/${inv.id}/pdf`} data-testid={`portal-invoice-pdf-${inv.id}`} className="text-[10px] font-bold uppercase tracking-widest text-white/50 hover:text-primary border border-white/15 hover:border-primary px-2 py-1 rounded transition-colors duration-200">PDF</a>
                              <span className="font-semibold text-sm">${inv.amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
                              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${inv.status === "paid" ? "bg-primary/15 text-primary" : "bg-amber-400/15 text-amber-300"}`}>{inv.status === "paid" ? "Paid" : "Due"}</span>
                            </span>
                          </div>
                        )) : <p className="text-[11px] text-white/35">No invoices yet.</p>}
                      </div>
                      )}

                      {tab === "messages" && (
                      <div className="mt-3 space-y-2" data-testid={`portal-clarifications-${j.id}`}>
                        {(!j.clarifications || j.clarifications.length === 0) && <p className="text-[11px] text-white/35">No questions right now.</p>}
                        {j.clarifications?.map((c) => (
                          <div key={c.id} className={`border rounded-lg px-3.5 py-3 ${c.status === "open" ? "border-amber-400/40 bg-amber-400/5" : "border-white/10 bg-white/[0.03]"}`} data-testid={`portal-clarification-${c.id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm text-white/85 flex-1">{c.question}</p>
                              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${c.status === "open" ? "bg-amber-400/15 text-amber-300" : "bg-primary/15 text-primary"}`} data-testid={`portal-clarification-status-${c.id}`}>{c.status === "open" ? "Open — needs you" : "Answered"}</span>
                            </div>
                            <p className="text-[10px] text-white/40 mt-1">{c.asked_by} · {fmtDateUS(c.asked_at)}</p>
                            {c.status === "closed" && (
                              <p className="text-xs text-white/65 mt-2 border-l-2 border-primary pl-2.5"><span className="text-primary font-semibold">You:</span> {c.answer}</p>
                            )}
                            {c.status === "open" && (
                              <div className="mt-3 space-y-2">
                                <textarea
                                  value={answers[c.id] || ""}
                                  onChange={(e) => setAnswers((a) => ({ ...a, [c.id]: e.target.value }))}
                                  rows={2}
                                  placeholder="Type your answer..."
                                  data-testid={`portal-answer-input-${c.id}`}
                                  className="w-full bg-white/5 border border-white/15 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary resize-none transition-colors duration-300"
                                />
                                <button onClick={() => sendAnswer(j.id, c.id)} disabled={sending === c.id} data-testid={`portal-answer-send-${c.id}`} className="bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest px-5 py-2.5 rounded-lg disabled:opacity-50">
                                  {sending === c.id ? "Sending..." : "Send Answer"}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      )}

                      {tab === "materials" && j.supply_mode === "labor" && (
                      <div className="mt-3 space-y-2" data-testid={`portal-materials-${j.id}`}>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">Material requests from the crew — you supply materials on this job</p>
                        {(j.material_orders || []).length === 0 && <p className="text-[11px] text-white/35">No material requests right now.</p>}
                        {(j.material_orders || []).map((o) => (
                          <div key={o.id} className={`border rounded-lg px-3.5 py-3 space-y-2 ${o.status === "pending" ? "border-amber-400/40 bg-amber-400/5" : "border-white/10 bg-white/[0.03]"}`} data-testid={`portal-mat-${o.id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-semibold">{o.item}{o.quantity ? ` · ${o.quantity}` : ""}</p>
                              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${o.status === "ordered" ? "bg-primary/15 text-primary" : "bg-amber-400/15 text-amber-300"}`} data-testid={`portal-mat-status-${o.id}`}>{o.status}</span>
                            </div>
                            <p className="text-[10px] text-white/40">{fmtDateUS(o.created_at)}{o.note ? ` · ${o.note}` : ""}</p>
                            {o.status === "ordered" ? (
                              <p className="text-[11px] text-white/60">{o.eta ? `ETA: ${o.eta}` : "Marked ordered"}{o.response_note ? ` — ${o.response_note}` : ""}</p>
                            ) : (
                              <div className="space-y-2">
                                <div className="flex gap-1.5">
                                  <input value={matAnswers[o.id]?.eta || ""} onChange={(e) => setMatAnswers({ ...matAnswers, [o.id]: { ...(matAnswers[o.id] || {}), eta: e.target.value } })} placeholder="ETA — e.g. Fri morning" data-testid={`portal-mat-eta-${o.id}`} className="rounded-lg flex-1 min-w-0 bg-white/5 border border-white/15 px-3 py-2 text-xs text-white placeholder:text-white/35 focus:outline-none focus:border-primary" />
                                  <input value={matAnswers[o.id]?.note || ""} onChange={(e) => setMatAnswers({ ...matAnswers, [o.id]: { ...(matAnswers[o.id] || {}), note: e.target.value } })} placeholder="Note" data-testid={`portal-mat-note-${o.id}`} className="rounded-lg flex-1 min-w-0 bg-white/5 border border-white/15 px-3 py-2 text-xs text-white placeholder:text-white/35 focus:outline-none focus:border-primary" />
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => answerMaterial(j.id, o.id, "ordered")} data-testid={`portal-mat-ordered-${o.id}`} className="flex-1 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest py-2.5 rounded-lg">Mark ordered</button>
                                  <button onClick={() => answerMaterial(j.id, o.id, "pending")} data-testid={`portal-mat-pending-${o.id}`} className="flex-1 border border-white/25 text-white/60 text-[10px] font-bold uppercase tracking-widest py-2.5 rounded-lg">Still pending</button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      )}
                      </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="mt-10 text-center text-white/35 text-xs">Questions? Call (203) 307-5222 · yali@infinitesiding.com</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
